import { Callout, LegacyPageBreadcrumb, EmptyState, useMediaQuery, AppShell, FullWidthPage } from "@wayflyer/flyui";
import { DatePickerHeader } from "@wayflyer/flyui-dataviz";
import { useIntl } from "react-intl";

import { InsightsDataTable } from "../components/insights-data-table";
import { DataGroupContainer, Spinner } from "../components/styled";

import type { InsightsSingleTableScreenProps } from "./interface";

/**
 * InsightsSingleTable component
 */
const InsightsSingleTable = ({
  dataLoading = false,
  dataError = "",
  tableProps,
  hint,
  breadcrumb,
  pageTitle,
  onSelectCampaignLink,
  datePickerHeaderProps,
  datePickerOnTop = false,
}: InsightsSingleTableScreenProps) => {
  const intl = useIntl();
  const noData = intl.formatMessage({
    defaultMessage: "No Data",
    id: "Single table screen no data message",
  });
  const query = useMediaQuery();
  const mediumWidth = query("sm").windowGt;
  const dataLoaded = !dataLoading && !dataError;
  const hasTable = dataLoaded && !!tableProps.columns?.length;
  // Preserve the original visibility: when `datePickerOnTop` it was always shown;
  // otherwise it lived in the table header, so it only appeared alongside the table
  // (not in the empty / loading / error states).
  const showDatePicker = datePickerOnTop || hasTable;

  const datePicker = (
    <DatePickerHeader
      summary={mediumWidth && !datePickerOnTop ? hint : ""}
      onChange={datePickerHeaderProps.onChange}
      currentPreset={datePickerHeaderProps.currentPreset}
      dateRangePresets={datePickerHeaderProps.dateRangePresets}
      startDate={datePickerHeaderProps.startDate}
      endDate={datePickerHeaderProps.endDate}
      useSimplePanel={true}
      inlineLeft={datePickerOnTop}
    />
  );

  const csvFilename = `wayflyer-insights-${
    pageTitle.toLowerCase().indexOf("product") >= 0 ? "products" : "channels"
  }-data.csv`;

  return (
    <AppShell>
      <FullWidthPage title={pageTitle} subtitle={datePickerOnTop ? hint : undefined}>
        <DataGroupContainer>
          {/* The date range refetches data server-side, so it's a page-level control
              rendered above the table (DataTable titles are strings, not nodes). */}
          {showDatePicker ? datePicker : null}
          {breadcrumb ? <LegacyPageBreadcrumb {...breadcrumb} /> : null}
          {hasTable ? (
            <InsightsDataTable
              {...tableProps}
              onClickLink={onSelectCampaignLink}
              enableCSVDownload={true}
              csvFilename={csvFilename}
            />
          ) : null}
          {dataLoaded && !tableProps.columns?.length ? <EmptyState text={noData} /> : null}
          {dataLoading && !dataError && <Spinner />}
          {dataError ? <Callout variant="critical" description={dataError} /> : null}
        </DataGroupContainer>
      </FullWidthPage>
    </AppShell>
  );
};

export const InsightsSingleTableScreen = (props: InsightsSingleTableScreenProps) => {
  return <InsightsSingleTable {...props} />;
};
